import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { api } from '../../api/client';

interface AuditEntry {
  id: string;
  user_id: string;
  user_name?: string;
  action: string;
  entity_type: string;
  entity_id: string;
  metadata: string | null;
  created_at: string;
}

export function AdminAuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<AuditEntry[]>('/admin/audit-log')
      .then((res) => setEntries(Array.isArray(res) ? res : (res as any).data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const actionColors: Record<string, string> = {
    gdpr_delete: 'bg-red-100 text-red-700',
    gdpr_export: 'bg-blue-100 text-blue-700',
    login: 'bg-green-100 text-green-700',
    register: 'bg-green-100 text-green-700',
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto mt-10 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">Audit Log</h1>
        <p className="text-mustard-charcoal/60 mb-8">Track all administrative actions</p>

        {loading ? (
          <p className="text-mustard-charcoal/60">Loading...</p>
        ) : entries.length === 0 ? (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
            <p className="text-mustard-charcoal/50">No audit log entries yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Time</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">User</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Action</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Entity</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-xs text-mustard-charcoal/50 whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString('en-GB')}
                    </td>
                    <td className="px-6 py-4 text-sm text-mustard-charcoal">
                      {entry.user_name || entry.user_id?.slice(0, 8)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${actionColors[entry.action] || 'bg-gray-100 text-gray-600'}`}>
                        {entry.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-mustard-charcoal/50">
                      {entry.entity_type} {entry.entity_id?.slice(0, 8)}
                    </td>
                    <td className="px-6 py-4 text-sm text-mustard-charcoal/60 max-w-md truncate">
                      {entry.metadata}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
